import React from "react";
import {
  Box,
  Chip,
  Stack,
  Typography,
  Button,
  MenuItem,
  TextField,
} from "@mui/material";
import SocialsCard from "../SocialsCard";
import type { ContentReviewItem } from "../../../services/contentReviewService";
import { groupReelVersions, positionOf } from "../../../lib/reelVersionChain";
import { stageOf, STAGE_LABELS, type SocialsStage } from "../../../lib/socialsStages";
import {
  deriveTypeOptions,
  deriveBuildingOptions,
  applyFilters,
  filterSummary,
  hasActiveFilter,
  NO_FILTERS,
  type SocialsFilterState,
  type FreshnessFilter,
} from "../../../lib/socialsFilters";
import ReelTile from "./ReelTile";
import SocialsListState from "../SocialsListState";

/**
 * Ch94 · THE REELS TAB.
 *
 * Stage pills, a derived filter strip, and ONE tile per reel with its versions
 * folded inside.
 *
 * 🔴 EVERY COUNT HERE IS COUNTED FROM WHAT IS LOADED, and the summary line says
 * so. `contentReviewService` fetches 24 with no pagination against a 29-card
 * queue, so a pill reading "Approved 0" could mean "none" or "none on this
 * page". Until the page holds the whole queue, the strip names the loaded set
 * rather than implying it covers everything.
 *
 * The stage pills are NOT the old status tabs. "Approved" is an applied P
 * command, not a status — there is no `approved` value in the data at all.
 */

interface Props {
  /**
   * 🔴 NULL means WE COULD NOT LOOK — not that there is nothing to review.
   * (Provider H1.) An empty array means we looked and the queue is genuinely
   * clear, which is good news; null means we have no idea, which is not. They
   * used to render identically.
   */
  items: ContentReviewItem[] | null;
  /** The queue total when the server reports one; null when unknown. */
  totalKnown: number | null;
  onReview: (item: ContentReviewItem) => void;
  onQuickVerdict?: (item: ContentReviewItem, verdict: "P" | "H") => void;
  /** A fetch is in flight and there is nothing to show yet. */
  loading?: boolean;
  /** A failure we are certain of. */
  error?: string | null;
  onRetry?: () => void;
  /**
   * The reel with a decision ON THE WIRE right now. Its quick actions are
   * disabled — Provider H2: without this a second click submits a second time.
   */
  busyId?: string | null;
  busyIds?: string[];
  unreadStages?: SocialsStage[];
}

/** The order Grant reads them in — his pipeline, left to right. */
const STAGE_ORDER: SocialsStage[] = [
  "to_review",
  "approved",
  "scheduled",
  "posted",
  "rejected",
  "replaced",
];

/** How many tiles to show before "Show more". */
const PAGE_SIZE = 12;

const ReelsTab: React.FC<Props> = ({
  items,
  totalKnown,
  onReview,
  onQuickVerdict,
  loading,
  error,
  onRetry,
  busyId, busyIds, unreadStages,
}) => {
  // Everything below works on a real array; the null case is answered by
  // SocialsListState at the bottom, before any of it is rendered.
  const safeItems = React.useMemo(() => items ?? [], [items]);
  const [stage, setStage] = React.useState<SocialsStage | null>(null);
  const [filters, setFilters] = React.useState<SocialsFilterState>(NO_FILTERS);
  const [visible, setVisible] = React.useState(PAGE_SIZE);

  // ONE tile per reel: versions collapse into their chain before anything else
  // counts them, so a three-version reel is one row of work, not three.
  const groups = React.useMemo(() => groupReelVersions(safeItems), [safeItems]);

  /** The head of each chain is what the grid shows. */
  const heads = React.useMemo(
    () => groups.map((group) => ({ group, item: group.head as ContentReviewItem })),
    [groups]
  );

  const stageOfHead = React.useCallback(
    (entry: (typeof heads)[number]): SocialsStage =>
      stageOf(entry.item, { chainPosition: positionOf(entry.group, entry.item) }).stage,
    []
  );

  const stageCounts = React.useMemo(() => {
    const counts = new Map<SocialsStage, number>();
    heads.forEach((entry) => {
      const key = stageOfHead(entry);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return counts;
  }, [heads, stageOfHead]);

  const filtered = React.useMemo(() => {
    const byStage = stage
      ? heads.filter((entry) => stageOfHead(entry) === stage)
      : heads;
    const allowed = new Set(
      applyFilters(
        byStage.map((entry) => entry.item),
        filters
      ).map((item) => item.id)
    );
    return byStage.filter((entry) => allowed.has(entry.item.id));
  }, [heads, stage, filters, stageOfHead]);

  const typeOptions = React.useMemo(
    () => deriveTypeOptions(heads.map((entry) => entry.item)),
    [heads]
  );
  const buildingOptions = React.useMemo(
    () => deriveBuildingOptions(heads.map((entry) => entry.item)),
    [heads]
  );

  const shown = filtered.slice(0, visible);

  const toggle = <T,>(current: T | null, next: T): T | null =>
    current === next ? null : next;

  return (
    <Stack spacing={2}>
      {/* STAGE PILLS — a count of what is loaded, never a fabricated 0. */}
      <Stack direction="row" gap={1} flexWrap="wrap" data-testid="stage-pills">
        {STAGE_ORDER.map((key) => {
          const count = unreadStages?.includes(key) ? "—" : stageCounts.get(key) ?? 0;
          // A stage with nothing in it is still shown — its absence is
          // information ("nothing is approved") and hiding it would make the
          // pipeline look shorter than it is.
          return (
            <Chip
              key={key}
              label={`${STAGE_LABELS[key]} ${count}`}
              color={stage === key ? "primary" : "default"}
              variant={stage === key ? "filled" : "outlined"}
              sx={{minHeight:44}}
              onClick={() => setStage((prev) => toggle(prev, key))}
            />
          );
        })}
      </Stack>

      {/*
        🔴 SELECTS, NOT CHIP ROWS — the control the approved mock uses, and the
        reason it does is now obvious: derived options are open-ended, so chips
        ran to roughly a thousand pixels at phone width and I had to hide them
        behind a "Filters" button I invented. Three dropdowns are compact at
        every width, so the workaround disappears with the problem.

        The OPTIONS are still derived from the loaded page. Only the control
        changed.
      */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        gap={1.5}
        alignItems={{ sm: "center" }}
        flexWrap="wrap"
      >
        <TextField
          select
          size="small"
          label="Type"
          value={filters.type ?? ""}
          onChange={(event) =>
            setFilters((prev) => ({ ...prev, type: event.target.value || null }))
          }
          sx={{ minWidth: 190 }}
          data-testid="type-filter"
        >
          <MenuItem value="">All types</MenuItem>
          {typeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label} ({option.count})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          // NOT "Building": the real building name does not reach this screen
          // (PKT-0796), so these values come from the subject or the file name.
          label="What it's about"
          value={filters.building ?? ""}
          onChange={(event) =>
            setFilters((prev) => ({ ...prev, building: event.target.value || null }))
          }
          sx={{ minWidth: 210 }}
          data-testid="building-filter"
        >
          <MenuItem value="">All</MenuItem>
          {buildingOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label} ({option.count})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="New or revised"
          value={filters.freshness}
          onChange={(event) =>
            setFilters((prev) => ({
              ...prev,
              freshness: event.target.value as FreshnessFilter,
            }))
          }
          sx={{ minWidth: 190 }}
          data-testid="freshness-filter"
        >
          <MenuItem value="all">New and revised</MenuItem>
          <MenuItem value="new">New only</MenuItem>
          <MenuItem value="revised">Revised from my note</MenuItem>
        </TextField>

        <Typography variant="caption" color="text.secondary" data-testid="filter-summary">
          {filterSummary(filtered.length, heads.length, totalKnown)}
        </Typography>
      </Stack>

      {/*
        FOUR STATES, not two. `items === null` is "we could not read the queue",
        which is a different fact from "the queue is clear" and used to render
        the same. Filters are handled separately below, because a filter that
        matches nothing is neither of those things — it is the filter's answer.
      */}
      {loading || error || items === null ? (
        <SocialsListState
          loading={loading}
          error={error}
          onRetry={onRetry}
          items={items}
          testIdPrefix="reels"
          emptyMessage="Nothing to review right now."
          unreadMessage="We couldn't read the review queue just now, so this is blank rather than empty."
        />
      ) : shown.length === 0 ? (
        <Typography color="text.secondary" data-testid="reels-empty">
          {hasActiveFilter(filters) || stage
            ? unreadStages?.includes(stage!) ? "This record has not been read yet. Its count is unknown." : "Nothing matches these filters."
            : "Nothing to review right now."}
        </Typography>
      ) : (
        <Box
          data-testid="reels-grid"
          sx={{
            display: "grid",
            // Mobile stays ONE simple column. The desktop expansion is extra
            // columns, never extra complexity.
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 2,
          }}
        >
          {shown.map((entry) => (
            <ReelTile
              key={entry.item.id}
              group={entry.group}
              item={entry.item}
              onReview={onReview}
              onQuickVerdict={onQuickVerdict}
              busy={busyId === entry.item.id || busyIds?.includes(entry.item.id)}
            />
          ))}
        </Box>
      )}

      {/*
        Pagination past the first screenful. The queue is 29 and the service
        fetches 24, so this bounds how many tiles mount at once — the tiles
        themselves then load media only when near the viewport.
      */}
      {filtered.length > visible ? (
        <Button
          onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
          data-testid="reels-show-more"
        >
          Show more ({filtered.length - visible} more)
        </Button>
      ) : null}
    </Stack>
  );
};

export default ReelsTab;
